"use client";

import { TrendingUp } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useEffect, useState } from "react";

export const description = "An area chart with gradient fill";

// Helper for Indian number formatting (lakh/crore)
function formatIndianNumber(num) {
  if (num === undefined || num === null) return '';
  num = Number(num);
  if (Math.abs(num) >= 1e7) return (num / 1e7).toFixed(2) + ' Cr';
  if (Math.abs(num) >= 1e5) return (num / 1e5).toFixed(2) + ' L';
  return num.toLocaleString('en-IN');
}

const chartConfig = {
  nifty: {
    label: "Nifty",
    color: "hsl(var(--chart-3))",
  },
  bank: {
    label: "BankNifty",
    color: "hsl(var(--chart-5))",
  },
  stock: {
    label: "Stocks",
    color: "hsl(var(--chart-1))",
  },

};

export function PNLChart({monthlyPnlData}) {
  const [timeRange, setTimeRange] = useState("all");

  const [filteredData, setFilteredData] = useState(monthlyPnlData);

  useEffect(() => {
    console.log("monthlyPnlData updated:", monthlyPnlData);
    setFilteredData(monthlyPnlData);
  }, [monthlyPnlData]);


  useEffect(() => {
    const monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };
  
    const newData = monthlyPnlData && monthlyPnlData?.filter((item) => {
      // Split the month and year from the "Dec-23" format
      const [monthStr, yearStr] = item.date.split("-");
      
      // Get the numeric month and full year
      const month = monthMap[monthStr];
      const year = parseInt(`20${yearStr}`, 10);
      
      // Create a Date object for the first day of the month
      const itemDate = new Date(year, month, 1); 
  
      const now = new Date();
      let monthsToSubtract = 12; // Default to 12 months
  
      // Adjust months based on the timeRange
      if (timeRange === "3m") {
        monthsToSubtract = 4;
      } else if (timeRange === "6m") {
        monthsToSubtract = 7;
      } else if (timeRange === "12m") {
        monthsToSubtract = 13;
      } else if (timeRange === "all") {
        return true; // If 'all' is selected, return all data
      }
  
      // Calculate the past date based on the months to subtract
      const pastDate = new Date(now);
      pastDate.setMonth(now.getMonth() - monthsToSubtract);
  
      // Filter data based on whether itemDate is greater than or equal to pastDate
      return itemDate >= pastDate;
    });
  
    // Set the filtered data
    setFilteredData(newData);
  }, [timeRange, monthlyPnlData]);
  
  
  
  console.log("length of filteredData:", filteredData?.length);

  return (
    <Card className="">
      <CardHeader className="flex items-center gap-2 space-y-0  py-5 sm:flex-row">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <CardTitle>Monthly PNL Chart</CardTitle>
          <CardDescription>
            Showing Total Monthly PNL for the last {timeRange !== "all" ? filteredData?.length : filteredData?.length} months
          </CardDescription>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger
            className="w-[160px] rounded-lg sm:ml-auto"
            aria-label="Select a value"
          >
            <SelectValue placeholder="Last 3 months" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all" className="rounded-lg">
              All
            </SelectItem>
            <SelectItem value="3m" className="rounded-lg">
              Last 3 months
            </SelectItem>
            <SelectItem value="6m" className="rounded-lg">
              Last 6 months
            </SelectItem>
            <SelectItem value="12m" className="rounded-lg">
              Last 12 months
            </SelectItem>
            
          </SelectContent>
        </Select>
      </CardHeader>

      <CardContent>
        <ChartContainer className="h-[500px] w-full mt-2" config={chartConfig}>
          <AreaChart
            accessibilityLayer
            data={filteredData}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.slice(0, 6)}
            />
           <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={1}
                tickCount={11}
                domain={["dataMin-200000", "dataMax+100000"]}
                tickFormatter={formatIndianNumber}
              />

            <ChartTooltip cursor={false} content={<ChartTooltipContent  />} />
            <defs>
              <linearGradient id="fillNifty" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-nifty)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-nifty)"
                  stopOpacity={0.1}
                />
              </linearGradient>
              <linearGradient id="fillBanknifty" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-bank)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-bank)"
                  stopOpacity={0.1}
                />
              </linearGradient>
              <linearGradient id="fillStock" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-stock)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-stock)"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <Area
              dataKey="bank"
              type="natural"
              fill="url(#fillBanknifty)"
              fillOpacity={0.4}
              stroke="var(--color-bank)"
              stackId="a"
            />
            <Area
              dataKey="nifty"
              type="natural"
              fill="url(#fillNifty)"
              fillOpacity={0.4}
              stroke="var(--color-nifty)"
              stackId="a"
            />
            <Area
              dataKey="stock"
              type="natural"
              fill="url(#fillStock)"
              fillOpacity={0.4}
              stroke="var(--color-stock)"
              stackId="a"
            />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
